import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import { PinIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { withStopPropagation } from '@/lib/event.util'
import { cn } from '@/lib/utils'

import { getAgencyInitials } from './agency.util'
import { AgencyVisibilityBadge } from './agency-visibility-badge.atom'

interface AgencyCardHeaderProps {
  agency: Agency
  isPinned: boolean
  isVisibilityBadgeVisible?: boolean
  onPin?: (agency: Agency) => void
}

export const AgencyCardHeader = ({
  agency,
  isPinned,
  isVisibilityBadgeVisible = false,
  onPin,
}: AgencyCardHeaderProps): ReactNode => (
  <div className="flex items-start gap-3 min-w-0">
    <Avatar className="agency-card-avatar size-10 rounded-lg shrink-0">
      {agency.logoUrl && <AvatarImage src={agency.logoUrl} alt={agency.name} />}
      <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-sm">
        {getAgencyInitials(agency.name)}
      </AvatarFallback>
    </Avatar>
    <div className="flex-1 min-w-0">
      <h3 className="agency-card-name font-semibold text-base leading-tight truncate">
        {agency.name}
      </h3>
      <div className="flex items-center gap-2 mt-1">
        <p className="agency-card-org text-xs font-normal text-muted-foreground truncate">
          {agency.organization.name}
        </p>
        {isVisibilityBadgeVisible && (
          <AgencyVisibilityBadge
            visibility={agency.visibility}
            className="text-[10px] px-1.5 py-0"
          />
        )}
      </div>
    </div>
    {onPin && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'agency-card-pin-button size-8 shrink-0 mt-[-14px] mr-[-14px]',
              isPinned && 'text-primary'
            )}
            onClick={withStopPropagation(() => onPin(agency))}
          >
            <PinIcon className={cn('size-4', isPinned && 'fill-current')} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isPinned ? 'Unpin agency' : 'Pin agency'}</TooltipContent>
      </Tooltip>
    )}
  </div>
)

AgencyCardHeader.displayName = 'AgencyCardHeader'
